<template>
  <main id="main" class="main">
    <section class="section">
      <div class="row">
        <div class="col-lg-12">
          <div class="card shadow">
            <div class="card-body p-4">
              <div class="row">
                <div class="col-sm-9">
                  <div class="pagetitle">
                    <h1 class="text-left">NUMBER CHECKER</h1>
                    <nav>
                      <ol class="breadcrumb breadJa">
                        <router-link :to="{ name: 'home' }" class="breadcrumb-item">Home</router-link>
                        <router-link :to="{ name: 'checker' }" class="breadcrumb-item">Number Checker</router-link>
                      </ol>
                    </nav>
                  </div>
                </div>
              </div>

              <!-- Form Input -->
              <div class="row mb-4">
                <div class="col-md-6">
                  <label for="instanceSelect" class="form-label">Select Instance</label>
                  <select 
                    id="instanceSelect" 
                    class="form-select" 
                    v-model="selectedInstance"
                    :disabled="loading || checking"
                  >
                    <option value="">-- Select Instance --</option>
                    <option 
                      v-for="instance in availableInstances" 
                      :key="instance.instanceId" 
                      :value="instance.instanceId"
                    >
                      {{ instance.instanceId.substring(0, 6) }} - {{ instance.phoneNumber || 'No Phone' }}
                    </option>
                  </select>
                </div>

                <div class="col-md-6">
                  <label for="phoneNumbers" class="form-label">Phone Numbers</label>
                  <textarea
                    id="phoneNumbers"
                    class="form-control"
                    rows="5"
                    v-model="phoneNumbers"
                    placeholder="Enter phone numbers (one per line)&#10;Example:&#10;62811704034&#10;6285172462123"
                    :disabled="checking"
                  ></textarea>
                  <small class="text-muted">
                    <i class="bi bi-info-circle me-1"></i>
                    Format: <strong>08xxx</strong> or <strong>62xxx</strong> (e.g., 08117040034 or 62811704034).
                  </small>
                </div>
              </div>

              <div class="row mb-4">
                <div class="col-md-12">
                  <button
                    class="btn btn-chatroom btn-primary-chatroom me-2"
                    @click="checkNumbers"
                    :disabled="!selectedInstance || !phoneNumbers.trim() || checking"
                  >
                    <i class="bi bi-check-circle"></i>
                    {{ checking ? 'Checking...' : 'Check Numbers' }}
                  </button>
                  <button
                    class="btn btn-chatroom btn-secondary-chatroom"
                    @click="clearForm"
                    :disabled="checking"
                  >
                    <i class="bi bi-x-circle"></i>
                    Clear
                  </button>
                </div>
              </div>

              <!-- Results Table -->
              <div v-if="results.length > 0" class="table-responsive">
                <table class="table table-hover shadow-sm table-bordered caption-top">
                  <caption class="pb-2 pt-1">Check Results</caption>
                  <thead class="table-primary">
                    <tr style="vertical-align:middle; text-align: center;">
                      <th scope="col">No</th>
                      <th scope="col">Phone Number</th>
                      <th scope="col">Status</th>
                      <th scope="col">JID</th>
                      <th scope="col">Note</th>
                    </tr>
                  </thead>
                  <tbody class="table-group-divider">
                    <tr v-if="checking">
                      <td colspan="5">
                        <div class="d-flex justify-content-center text-primary m-3">
                          <strong role="status" class="pt-1" style="padding-right: 2rem;">Checking numbers...</strong>
                          <div class="spinner-border shadow" aria-hidden="true"></div>
                        </div>
                      </td>
                    </tr>
                    <tr
                      v-else
                      v-for="(result, index) in results"
                      :key="index"
                      style="vertical-align:middle;"
                    >
                      <th scope="row" width="5%" style="text-align: center;">{{ index + 1 }}</th>
                      <td style="text-align: center;">{{ result.phone || result.number }}</td>
                      <td style="text-align: center;">
                        <span
                          class="badge rounded-pill"
                          :class="{
                            'bg-success': result.isRegistered,
                            'bg-warning text-dark': !result.isRegistered && result.willSkipValidation && !result.error,
                            'bg-danger': !result.isRegistered && !result.willSkipValidation && !result.error,
                            'bg-secondary': result.error
                          }"
                          :title="result.willSkipValidation ? 'Will skip validation when sending' : ''"
                        >
                          {{ result.error ? 'Error' : (result.isRegistered ? 'Registered' : (result.willSkipValidation ? 'Unregistered (Will Try)' : 'Not Registered')) }}
                        </span>
                      </td>
                      <td style="text-align: left; font-size: 0.85rem;">
                        {{ result.jid || '-' }}
                      </td>
                      <td style="text-align: left; font-size: 0.85rem;">
                        <span v-if="result.note" class="text-muted">
                          <i class="bi bi-info-circle me-1"></i>
                          {{ result.note }}
                        </span>
                        <span v-else>-</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Summary -->
              <div v-if="results.length > 0 && !checking" class="row mt-3">
                <div class="col-12">
                  <div class="alert alert-info">
                    <strong>Summary:</strong> 
                    Total: {{ results.length }} | 
                    Registered: {{ results.filter(r => r.isRegistered).length }} | 
                    Not Registered: {{ results.filter(r => !r.isRegistered && !r.error).length }} |
                    Will Skip Validation: {{ results.filter(r => r.willSkipValidation).length }} |
                    Errors: {{ results.filter(r => r.error).length }}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      selectedInstance: '',
      phoneNumbers: '',
      availableInstances: [],
      results: [],
      loading: false,
      checking: false,
      baseUrl: process.env.VUE_APP_BE_APP_BASE_URL,
    };
  },
  mounted() {
    this.fetchInstances();
  },
  methods: {
    async fetchInstances() {
      try {
        this.loading = true;
        const response = await axios.get(`${this.baseUrl}/api/instances`, {
          params: { all: true }
        });
        
        const instances = response.data.data.instances || [];
        // Filter only online instances
        this.availableInstances = instances.filter(inst => inst.status === 'online');
        
      } catch (error) {
        console.error('Failed to fetch instances:', error);
        this.Toasttt('Failed to load instances', 'error');
      } finally {
        this.loading = false;
      }
    },

    async checkNumbers() {
      if (!this.selectedInstance || !this.phoneNumbers.trim()) {
        this.Toasttt('Please select an instance and enter phone numbers', 'warning');
        return;
      }

      // Parse phone numbers
      const numbers = this.phoneNumbers
        .split('\n')
        .map(num => num.trim())
        .filter(num => num.length > 0)
        .map(num => this.formatPhoneNumber(num)); // Auto-format numbers

      if (numbers.length === 0) {
        this.Toasttt('No valid phone numbers found', 'warning');
        return;
      }

      this.checking = true;
      this.results = [];

      try {
        // Check each number
        for (const number of numbers) {
          try {
            const response = await axios.post(
              `${this.baseUrl}/api/check/${this.selectedInstance}`,
              {
                phone: number
              }
            );

            if (response.data.success) {
              this.results.push({
                number: number,
                isRegistered: response.data.data?.isRegistered || false,
                jid: response.data.data?.jid || '-',
                phone: response.data.data?.phone || number,
                note: response.data.data?.note || '',
                willSkipValidation: response.data.data?.willSkipValidation || false,
                error: false
              });
            } else {
              this.results.push({
                number: number,
                isRegistered: false,
                jid: '-',
                phone: number,
                note: response.data.message || 'Check failed',
                willSkipValidation: false,
                error: true
              });
            }
          } catch (error) {
            this.results.push({
              number: number,
              isRegistered: false,
              jid: error.response?.data?.message || 'Request failed',
              phone: number,
              note: error.response?.data?.message || 'Request failed',
              willSkipValidation: false,
              error: true
            });
          }
        }

        this.Toasttt('Number check completed', 'success');
      } catch (error) {
        console.error('Error checking numbers:', error);
        this.Toasttt('Failed to check numbers', 'error');
      } finally {
        this.checking = false;
      }
    },

    clearForm() {
      this.phoneNumbers = '';
      this.selectedInstance = '';
      this.results = [];
    },

    formatPhoneNumber(number) {
      // Remove all spaces, dashes, and special characters
      let cleaned = number.replace(/[\s\-\(\)]/g, '');
      
      // If number starts with 0 (Indonesian format), replace with 62
      if (cleaned.startsWith('0')) {
        cleaned = '62' + cleaned.substring(1);
      }
      
      // If number starts with +, remove it
      if (cleaned.startsWith('+')) {
        cleaned = cleaned.substring(1);
      }
      
      return cleaned;
    },

    Toasttt(message, type) {
      // Use your existing toast notification system
      if (this.$swal) {
        this.$swal.fire({
          toast: true,
          position: 'top-end',
          icon: type,
          title: message,
          showConfirmButton: false,
          timer: 3000
        });
      }
    }
  }
};
</script>

<style scoped>
.breadJa {
  margin-bottom: 0;
}

.fade-in {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
